import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	Alert,
	Box,
	Button,
	Group,
	LoadingOverlay,
	ScrollArea,
	SegmentedControl,
	Table,
	Text,
} from '@mantine/core';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { SCUD_MODE_LABELS, SCUD_PAGE_SIZES } from '@/shared/constant/scudMapping';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import {
	IScudMapping,
	IScudMappingPermissions,
	TScudMappingFilters,
	TScudMappingListQuery,
	TScudMappingMode,
	TScudMappingSort,
} from '@/shared/types/scudMapping.types';

import { IconRefresh } from '@tabler/icons-react';

import { ScudMappingEditForm } from './ScudMappingEditForm';
import { EMPTY_SCUD_FILTERS, ScudMappingFilters } from './ScudMappingFilters';
import { ScudMappingRow } from './ScudMappingRow';
import { ScudQueueBar } from './ScudQueueBar';
import { ScudTable, ScudTablePager, ScudTh, pageCount, useTableSort } from './ScudTable';
import { SortableTh } from './SortableTh';
import { SCUD_MAPPING_DETAILS_KEY, SCUD_MAPPING_LIST_KEY, SCUD_REASONS_ALL_KEY } from './queryKeys';
import { reasonLabelById, requestErrorText } from './utils';

const toQuery = (
	mode: TScudMappingMode,
	filters: TScudMappingFilters,
	sort: TScudMappingSort,
	desc: boolean,
	page: number,
	pageSize: number,
): TScudMappingListQuery => ({
	mode,
	status: filters.statuses.length ? filters.statuses : undefined,
	location: filters.locations.length ? filters.locations : undefined,
	last_pass_from: filters.last_pass_from
		? dayjs(filters.last_pass_from).format('YYYY-MM-DD')
		: undefined,
	last_pass_to: filters.last_pass_to ? dayjs(filters.last_pass_to).format('YYYY-MM-DD') : undefined,
	search: filters.search.trim() || undefined,
	reason_id: filters.reason_ids.length ? filters.reason_ids : undefined,
	confirmed_by: mode === 'processed' ? filters.confirmed_by.trim() || undefined : undefined,
	sort,
	desc,
	offset: (page - 1) * pageSize,
	limit: pageSize,
});

type ScudMappingListProps = {
	permissions: IScudMappingPermissions;
};

export const ScudMappingList = ({ permissions }: ScudMappingListProps) => {
	const queryClient = useQueryClient();
	const [mode, setMode] = useState<TScudMappingMode>('unprocessed');
	const [draft, setDraft] = useState<TScudMappingFilters>(EMPTY_SCUD_FILTERS);
	const [filters, setFilters] = useState<TScudMappingFilters>(EMPTY_SCUD_FILTERS);
	const [page, setPage] = useState(1);
	// По умолчанию — по дате появления, старые сверху (ТЗ 8.2)
	const { sort, desc, onSort } = useTableSort<TScudMappingSort>('created_at', () => setPage(1));
	const [pageSize, setPageSize] = useState(Number(SCUD_PAGE_SIZES[0]));
	const [editId, setEditId] = useState<number | null>(null);
	const [confirmingIds, setConfirmingIds] = useState<number[]>([]);

	const query = useMemo(
		() => toQuery(mode, filters, sort, desc, page, pageSize),
		[mode, filters, sort, desc, page, pageSize],
	);

	const { data, isFetching, isLoading, isError, error, refetch } = useQuery({
		queryKey: [...SCUD_MAPPING_LIST_KEY, query],
		queryFn: () => scudMappingApi.getList(query),
		placeholderData: keepPreviousData,
	});

	const { data: reasons = [] } = useQuery({
		queryKey: SCUD_REASONS_ALL_KEY,
		queryFn: () => scudMappingApi.getReasons(false),
		staleTime: 300_000,
	});

	// При ошибке не показываем прошлую страницу как актуальную
	const mappings = isError ? [] : (data?.objects ?? []);
	const total = data?.total_amount ?? 0;
	const unprocessed = data?.unprocessed_by_location ?? [];
	const unprocessedTotal = unprocessed.reduce((sum, item) => sum + item.count, 0);
	const pages = pageCount(total, pageSize);

	// Список сократился (кто-то обработал записи) — встаём на последнюю страницу
	useEffect(() => {
		if (data && page > pages) {
			setPage(pages);
		}
	}, [data, page, pages]);

	const { mutate: confirm } = useMutation({
		mutationFn: scudMappingApi.confirm,
		onMutate: ({ id }) => setConfirmingIds((prev) => [...prev, id]),
		onSuccess: () => useShowNotifaction({ text: 'Запись подтверждена', type: 'success' }),
		onError: (mutationError) =>
			useShowNotifaction({
				text: requestErrorText(mutationError, 'Не удалось подтвердить запись'),
				type: 'error',
			}),
		onSettled: (_, __, { id }) => {
			setConfirmingIds((prev) => prev.filter((item) => item !== id));
			void queryClient.invalidateQueries({ queryKey: SCUD_MAPPING_LIST_KEY });
			// Карточка с прежней версией дала бы лишний 409 при открытии формы
			void queryClient.invalidateQueries({ queryKey: [...SCUD_MAPPING_DETAILS_KEY, id] });
		},
	});

	const handleConfirm = useCallback(
		(mapping: IScudMapping) => confirm({ id: mapping.id, version: mapping.version }),
		[confirm],
	);

	const handleEdit = useCallback((id: number) => setEditId(id), []);

	const applyFilters = (value: TScudMappingFilters) => {
		setDraft(value);
		setFilters(value);
		setPage(1);
	};

	const handleMode = (value: string) => {
		setMode(value as TScudMappingMode);
		setPage(1);
	};

	// Берём черновик, чтобы не потерять ещё не применённые правки фильтров
	const handleLocationChip = (location: string) => {
		const isActive = filters.locations.length === 1 && filters.locations[0] === location;

		applyFilters({ ...draft, locations: isActive ? [] : [location] });
		setMode('unprocessed');
	};

	const thProps = { sort, desc, onSort };
	const isFiltered = JSON.stringify(filters) !== JSON.stringify(EMPTY_SCUD_FILTERS);
	const emptyText = (() => {
		if (isFiltered) {
			return 'По этим фильтрам записей нет. Измените условия или нажмите «Сбросить».';
		}

		return mode === 'unprocessed'
			? 'Все пропуска обработаны. Новые появятся после следующей загрузки.'
			: 'Записей пока нет.';
	})();

	return (
		<Box
			pos="relative"
			style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden', flex: 1 }}
		>
			<LoadingOverlay visible={isLoading} zIndex={100} overlayProps={{ radius: 2, blur: 2 }} />
			<Group justify="space-between">
				<SegmentedControl
					radius="lg"
					value={mode}
					onChange={handleMode}
					data={(Object.keys(SCUD_MODE_LABELS) as TScudMappingMode[]).map((item) => ({
						value: item,
						label:
							item === 'unprocessed' ? (
								// Только строчный текст: ширину сегмента Mantine считает по нему, блок с бейджем обрезается
								<>
									{SCUD_MODE_LABELS[item]}{' '}
									<Text span inherit fw={800} c="orange.7">
										{unprocessedTotal}
									</Text>
								</>
							) : (
								SCUD_MODE_LABELS[item]
							),
					}))}
				/>
				<Button
					variant="default"
					radius="lg"
					leftSection={<IconRefresh size={16} />}
					loading={isFetching && !isLoading}
					onClick={() => void refetch()}
				>
					Обновить
				</Button>
			</Group>

			{data && !isError && (
				<ScudQueueBar
					items={unprocessed}
					active={filters.locations.length === 1 ? filters.locations[0] : null}
					onSelect={handleLocationChip}
				/>
			)}

			<ScudMappingFilters
				value={draft}
				onChange={setDraft}
				onApply={() => applyFilters(draft)}
				onReset={() => applyFilters(EMPTY_SCUD_FILTERS)}
				mode={mode}
				locations={permissions.locations}
				reasons={reasons}
			/>

			{isError ? (
				<Alert color="red" variant="light">
					{requestErrorText(error, 'Не удалось загрузить записи')}
				</Alert>
			) : (
				<Text size="sm" c="dimmed">
					Найдено: {total}
					{/* У сортировки по умолчанию нет колонки, поэтому порядок называем словами */}
					{sort === 'created_at' && !desc && '. Сначала записи, появившиеся раньше всех'}
				</Text>
			)}

			<ScrollArea style={{ flex: 1 }} scrollbarSize={12} offsetScrollbars>
				<ScudTable miw={1180}>
					<Table.Thead>
						<Table.Tr>
							<SortableTh {...thProps} width={48}>
								№
							</SortableTh>
							<SortableTh {...thProps} field="location">
								Локация
							</SortableTh>
							<SortableTh {...thProps} field="company">
								Фирма (СКУД)
							</SortableTh>
							<SortableTh {...thProps} field="pass">
								Пропуск
							</SortableTh>
							<SortableTh {...thProps} field="full_name_skud">
								Сотрудник по СКУД
							</SortableTh>
							<SortableTh {...thProps} field="match_status">
								Статус
							</SortableTh>
							<SortableTh {...thProps} field="full_name_federation">
								Сотрудник в «Федерации»
							</SortableTh>
							<SortableTh {...thProps} field="last_pass">
								Последний проход
							</SortableTh>
							<SortableTh {...thProps} field="reason">
								Причина
							</SortableTh>
							<SortableTh {...thProps} field="confirmed_at">
								Подтверждено
							</SortableTh>
							<ScudTh>Действия</ScudTh>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{mappings.map((mapping, index) => (
							<ScudMappingRow
								key={mapping.id}
								mapping={mapping}
								reasonLabel={reasonLabelById(reasons, mapping.reason_id, mapping.reason_name)}
								number={(page - 1) * pageSize + index + 1}
								isConfirming={confirmingIds.includes(mapping.id)}
								isPendingMarked={mode === 'all' && !mapping.guard_confirmed}
								onConfirm={handleConfirm}
								onEdit={handleEdit}
							/>
						))}
					</Table.Tbody>
				</ScudTable>
				{!isLoading && !isError && !mappings.length && (
					<Text c="dimmed" ta="center" py="xl">
						{emptyText}
					</Text>
				)}
			</ScrollArea>

			<ScudTablePager
				page={page}
				pageSize={pageSize}
				total={total}
				onPageChange={setPage}
				onPageSizeChange={(value) => {
					setPageSize(value);
					setPage(1);
				}}
			/>

			<ScudMappingEditForm mappingId={editId} onClose={() => setEditId(null)} />
		</Box>
	);
};
